import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useState, useCallback } from 'react';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError, AutocompleteWrapper, AutocompleteDropdown, SuggestionItem, SuggestionCusip, SuggestionSecurity } from './shared/TicketStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars } from '../../utils/settlement';
import { parseAmountShorthand } from '../../utils/amountUtils';
import { useTenantConfig } from '../../contexts/TenantConfigContext';
import axios from 'axios';
import styled from 'styled-components';
import { BarChart3, Briefcase, LineChart } from 'lucide-react';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  EconomicsPreview,
  StructuredBand,
  StructuredHeroPanel,
  StructuredWorkspace,
  TradeEconomicsGrid,
  formatCurrencyValue,
  formatNumberValue,
} from './shared/StructuredCreditTicketLayout';

import { DateField } from '../shared/DateField';

// Market Data Panel
const MarketDataPanel = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(180deg, rgba(14, 165, 233, 0.11) 0%, rgba(2, 6, 23, 0.2) 100%);
  border-radius: 12px;
  border: 1px solid rgba(14, 165, 233, 0.28);
`;

const MarketDataTitle = styled.h3`
  font-size: 13px;
  font-weight: 850;
  color: #7dd3fc;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const MarketDataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const MarketDataCard = styled.div`
  background: rgba(15, 23, 42, 0.62);
  padding: 13px;
  border-radius: 10px;
  border: 1px solid rgba(71, 85, 105, 0.36);
`;

const MarketDataLabel = styled.div`
  font-size: 10px;
  font-weight: 850;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
`;

const MarketDataValue = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-size: 18px;
  font-weight: 900;
  color: ${props => {
    if (props.positive) return '#4ade80';
    if (props.negative) return '#f87171';
    return '#f1f5f9';
  }};
`;

const MarketDataChange = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-size: 13px;
  color: ${props => {
    if (props.positive) return '#4ade80';
    if (props.negative) return '#f87171';
    return '#94a3b8';
  }};
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// Stock Metrics Panel
const StockMetricsPanel = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(180deg, rgba(34, 197, 94, 0.1) 0%, rgba(2, 6, 23, 0.2) 100%);
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.26);
`;

const StockMetricsTitle = styled.h3`
  font-size: 13px;
  font-weight: 850;
  color: #86efac;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const StockMetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
`;

const MetricCard = styled.div`
  background: rgba(15, 23, 42, 0.62);
  padding: 13px;
  border-radius: 10px;
  border: 1px solid rgba(71, 85, 105, 0.36);
`;

const MetricLabel = styled.div`
  font-size: 10px;
  font-weight: 850;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
`;

const MetricValue = styled.div`
  font-size: 16px;
  font-weight: 900;
  color: #f1f5f9;
`;

const LoadingMessage = styled.div`
  color: #64748b;
  font-size: 14px;
  font-style: italic;
`;

// Order Type Selector
const OrderTypeSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const OrderTypeButton = styled.button<{ $active?: boolean }>`
  min-height: 40px;
  padding: 9px 16px;
  border: 1.5px solid ${props => props.$active ? '#0ea5e9' : 'rgba(148, 163, 184, 0.28)'};
  background: ${props => props.$active ? 'rgba(14, 165, 233, 0.16)' : 'rgba(15, 23, 42, 0.42)'};
  color: ${props => props.$active ? '#7dd3fc' : '#94a3b8'};
  border-radius: 10px;
  font-weight: 850;
  font-size: 12px;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    color: #7dd3fc;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.52;
    cursor: not-allowed;
  }
`;

const ExecutionNotice = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.22);
  background: rgba(34, 197, 94, 0.08);
  margin-bottom: 18px;
`;

/**
 * Settle date query for a listed equity (ADR 0009 §4.2): US equities settle T+1 (since 2024-05-28)
 * on the NYSE calendar. The server stores the same date.
 */
const equitySettlementQuery = (tradeDate: string, currency?: string | null) => ({
  tradeDate,
  ticketType: 'PUBLIC_FI',
  securityType: 'EQUITY',
  convention: 'T+1',
  currency: currency || 'USD',
});

/**
 * While the settle date is still the one this ticket set (a new ticket, or after a trade date
 * change), shows the browser's count at once and replaces it with the server's answer. A settle
 * date the user typed, or one loaded from a stored trade, is left alone.
 */
const EquitySettleDateEffect: React.FC<{
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  autoSettleRef: React.MutableRefObject<string | null>;
}> = ({ formData, setFormData, autoSettleRef }) => {
  React.useEffect(() => {
    if (!formData.tradeDate || formData.settleDate !== autoSettleRef.current) return undefined;
    const query = equitySettlementQuery(formData.tradeDate, formData.currency);
    const provisional = provisionalSettleDate(query);
    autoSettleRef.current = provisional;
    if (provisional !== formData.settleDate) {
      setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
    }
    let current = true;
    resolveSettleDate(query).then(settleDate => {
      if (!current || !settleDate) return;
      setFormData((prev: any) => {
        // Only if the form still holds what was asked, and nobody typed over the automatic date.
        if (prev.tradeDate !== query.tradeDate || (prev.currency || 'USD') !== query.currency
            || prev.settleDate !== autoSettleRef.current) {
          return prev;
        }
        autoSettleRef.current = settleDate;
        return { ...prev, settleDate };
      });
    });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tradeDate, formData.currency]);
  return null;
};

const EquityTicketScreen: React.FC = () => {
  const [tickerSuggestions, setTickerSuggestions] = useState<any[]>([]);
  // Why the trade date field is empty although a date was typed (a weekend or holiday), with the next business day.
  const [tradeDateProblem, setTradeDateProblem] = useState<{ problem: string; fix: { iso: string; label: string } | null } | null>(null);
  const [showTickerSuggestions, setShowTickerSuggestions] = useState(false);
  const [marketData, setMarketData] = useState<any>(null);
  const { isRetail } = useTenantConfig();
  const [marketDataLoading, setMarketDataLoading] = useState(false);
  const [stockMetrics, setStockMetrics] = useState<any>(null);

  const initialTradeDate = getTodayDate(calendarsFor('PUBLIC_FI', 'USD', null, 'EQUITY'));
  const initialSettleDate = provisionalSettleDate(equitySettlementQuery(initialTradeDate, 'USD'));
  // The settle date this ticket last set by itself; null once it came from a stored trade.
  const autoSettleRef = React.useRef<string | null>(initialSettleDate);

  const initialFormData = {
    transactionType: 'BUY' as 'BUY' | 'SELL',
    assetManager: '',
    portfolioManager: '',
    assetClass: 'Equity',
    assetSubClass: 'Common Stock',
    security: '',
    ticker: '',
    cusip: '',
    price: '',
    limitPrice: '',
    stopPrice: '',
    portfolio: '',
    shares: '',
    tradeDate: initialTradeDate,
    settleDate: initialSettleDate, // T+1 for US equities, on the NYSE calendar
    submittedBy: getCurrentOperatorName(),
    currency: 'USD',
    executedBy: '',
    notes: '',
    rationale: '',
    orderType: 'MARKET',
    // Execution venue fields
    executionBroker: '',
    ecnPlatform: '',
    executionVenue: '',
    executionId: '',
    executionTime: ''
  };

  // Fetch ticker suggestions
  const fetchTickerSuggestions = async (prefix: string) => {
    if (!prefix || prefix.length < 1) {
      setTickerSuggestions([]);
      return;
    }

    try {
      // Search by ticker first
      const response = await axios.get(`/api/dimensions/securities/search?q=${encodeURIComponent(prefix)}&assetClass=Equity&limit=10`);
      setTickerSuggestions(response.data || []);
      if (response.data && response.data.length > 0) {
        setShowTickerSuggestions(true);
      }
    } catch (error: any) {
      console.error('Error fetching ticker suggestions:', error);
      // Fallback to CUSIP search
      try {
        const cusipResponse = await axios.get(`/api/dimensions/securities/cusip/search?prefix=${encodeURIComponent(prefix)}`);
        const equitySecurities = (cusipResponse.data || []).filter((s: any) =>
          s.assetClass?.assetClassName === 'Equity' || s.assetClass === 'Equity'
        );
        setTickerSuggestions(equitySecurities);
        if (equitySecurities.length > 0) {
          setShowTickerSuggestions(true);
        }
      } catch (e) {
        setTickerSuggestions([]);
        setShowTickerSuggestions(false);
      }
    }
  };

  // Fetch market data for selected security
  const fetchMarketData = useCallback(async (ticker: string, cusip: string) => {
    if (!ticker && !cusip) return;

    setMarketDataLoading(true);
    try {
      // Try to get market data from the API
      const identifier = ticker || cusip;
      const response = await axios.get(`/api/market-data/quote/${identifier}`);
      setMarketData(response.data);

      // Also get stock metrics
      const metricsResponse = await axios.get(`/api/market-data/metrics/${identifier}`);
      setStockMetrics(metricsResponse.data);
    } catch (error) {
      console.log('Market data not available, using security master data');
      // Fallback to security master data
      try {
        const identifier = ticker || cusip;
        const isCusip = /^[A-Z0-9]{9}$/i.test(identifier);
        const securityResponse = await axios.get(
          isCusip ? `/api/dimensions/securities/cusip/${identifier}` : `/api/dimensions/securities/search?q=${encodeURIComponent(identifier)}&limit=1`
        );
        const securityData = isCusip ? securityResponse.data : (securityResponse.data?.[0] || null);
        if (!securityData) throw new Error('Security not found');
        const security = securityData;
        setMarketData({
          lastPrice: security.lastPrice,
          previousClose: security.previousClose,
          change: security.priceChange,
          changePercent: security.priceChangePercent,
          volume: security.volume,
          avgVolume: security.avgVolume,
          bid: security.bidPrice,
          ask: security.askPrice,
          high52Week: security.high52Week,
          low52Week: security.low52Week
        });
        setStockMetrics({
          marketCap: security.marketCap,
          peRatio: security.peRatio,
          dividendYield: security.dividendYield,
          beta: security.beta,
          eps: security.eps
        });
      } catch (e) {
        setMarketData(null);
        setStockMetrics(null);
      }
    } finally {
      setMarketDataLoading(false);
    }
  }, []);

  const handleTickerSelect = async (security: any, setFormData: any, setErrors: any, errors: any, dimensionData: any) => {
    const ticker = security.ticker || '';
    const cusip = security.cusip || '';

    setFormData((prev: any) => ({
      ...prev,
      ticker: ticker,
      cusip: cusip,
      security: security.securityName || security.ticker || security.cusip,
      currency: security.currency || 'USD'
    }));
    setShowTickerSuggestions(false);
    setTickerSuggestions([]);

    // Clear errors
    if (errors.ticker) {
      setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors.ticker;
        return newErrors;
      });
    }

    // Fetch market data
    fetchMarketData(ticker, cusip);

    // Set asset class/subclass
    if (security.assetClass) {
      const assetClassName = typeof security.assetClass === 'string'
        ? security.assetClass
        : security.assetClass.assetClassName;
      setFormData((prev: any) => ({ ...prev, assetClass: assetClassName || 'Equity' }));
    }
    if (security.assetSubClass) {
      const assetSubClassName = typeof security.assetSubClass === 'string'
        ? security.assetSubClass
        : security.assetSubClass.assetSubClassName;
      setFormData((prev: any) => ({ ...prev, assetSubClass: assetSubClassName || 'Common Stock' }));
    }
  };

  const validateForm = (formData: any, action: string, useAllocations: boolean = false): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};

    const validateField = (fieldName: string, value: string): string => {
      switch (fieldName) {
        case 'assetManager':
          if (!value?.trim()) return 'Asset Manager is required';
          break;
        case 'portfolioManager':
          if (!value?.trim()) return 'Portfolio Manager is required';
          break;
        case 'assetClass':
          if (!value?.trim()) return 'Asset Class is required';
          break;
        case 'security':
          if (!value?.trim()) return 'Security name is required';
          break;
        case 'ticker':
          if (!formData.cusip && !value?.trim()) return 'Ticker or CUSIP is required';
          break;
        case 'portfolio':
          if (!useAllocations && !value?.trim()) return 'Portfolio is required';
          break;
        case 'shares':
          if (!value || value.trim() === '') return 'Number of shares is required';
          const sharesNum = parseFloat(value);
          if (isNaN(sharesNum) || sharesNum <= 0) return 'Shares must be greater than 0';
          break;
        case 'price':
          if (action === 'execute' && (!value || value.trim() === '')) {
            return 'Price is required for execution';
          }
          if (value && value.trim() !== '') {
            const priceNum = parseFloat(value);
            if (isNaN(priceNum) || priceNum <= 0) return 'Price must be greater than 0';
          }
          break;
        case 'limitPrice':
          if (formData.orderType === 'LIMIT' && (!value || parseFloat(value) <= 0)) {
            return 'Limit price is required for limit orders';
          }
          break;
        case 'stopPrice':
          if (formData.orderType === 'STOP' && (!value || parseFloat(value) <= 0)) {
            return 'Stop price is required for stop orders';
          }
          break;
        case 'executedBy':
          if (action === 'execute' && !value?.trim()) {
            return 'Executed By is required for execution';
          }
          break;
        case 'tradeDate':
          if (!value) return tradeDateProblem ? `${tradeDateProblem.problem}${tradeDateProblem.fix ? ` — use ${tradeDateProblem.fix.label}` : ''}` : 'Trade Date is required';
          const tradeDate = new Date(value);
          if (isNaN(tradeDate.getTime())) return 'Invalid trade date';
          break;
        case 'settleDate':
          if (!value) return 'Settle Date is required';
          const settleDate = new Date(value);
          if (isNaN(settleDate.getTime())) return 'Invalid settle date';
          if (formData.tradeDate) {
            const tDate = new Date(formData.tradeDate);
            if (settleDate < tDate) return 'Settle date cannot be before trade date';
          }
          break;
      }
      return '';
    };

    if (action === 'execute') {
      const priceError = validateField('price', formData.price || '');
      if (priceError) errors.price = priceError;

      const executedByError = validateField('executedBy', formData.executedBy || '');
      if (executedByError) errors.executedBy = executedByError;
    }

    if (action !== 'execute') {
      // Retail clients don't need Asset Manager (RIA is the only manager)
      const fieldsToValidate = isRetail
        ? ['portfolioManager', 'assetClass', 'security', 'ticker', 'shares', 'tradeDate', 'settleDate']
        : ['assetManager', 'portfolioManager', 'assetClass', 'security', 'ticker', 'shares', 'tradeDate', 'settleDate'];

      if (!useAllocations) {
        fieldsToValidate.push('portfolio');
      }

      // Add limit/stop price validation
      if (formData.orderType === 'LIMIT') {
        fieldsToValidate.push('limitPrice');
      } else if (formData.orderType === 'STOP') {
        fieldsToValidate.push('stopPrice');
      }

      fieldsToValidate.forEach(field => {
        const error = validateField(field, formData[field] || '');
        if (error) {
          errors[field] = error;
        }
      });
    }

    return errors;
  };

  const buildTransactionData = async (formData: any, dimensionData: any, useAllocations: boolean = false): Promise<any> => {
    // Validate required fields
    if (!formData.ticker && !formData.cusip) {
      throw new Error('Ticker or CUSIP is required');
    }
    if (!formData.shares || !formData.shares.toString().trim()) {
      throw new Error('Number of shares is required');
    }

    // Look up security by ticker or CUSIP
    let securityId: number | null = null;
    try {
      let securityResponse;
      const identifier = (formData.ticker || formData.cusip || '').trim();

      // Detect if identifier looks like a CUSIP (9 alphanumeric chars, typically starts with digit)
      // CUSIPs are 9 chars, tickers are usually 1-5 chars
      const isCusip = /^[A-Z0-9]{9}$/i.test(identifier);

      if (isCusip) {
        // Use CUSIP endpoint
        securityResponse = await axios.get(`/api/dimensions/securities/cusip/${identifier}`);
      } else {
        // Use search endpoint for ticker-like identifiers
        const searchResponse = await axios.get(`/api/dimensions/securities/search?q=${encodeURIComponent(identifier)}&limit=1`);
        if (searchResponse.data && searchResponse.data.length > 0) {
          securityResponse = { data: searchResponse.data[0] };
        } else {
          throw new Error(`Security not found for ticker: ${identifier}`);
        }
      }
      if (securityResponse.data && securityResponse.data.id) {
        securityId = securityResponse.data.id;
      } else {
        throw new Error(`Security not found`);
      }
    } catch (error: any) {
      const identifier = formData.ticker || formData.cusip;
      const errorMsg = error.response?.status === 404
        ? `Security with identifier ${identifier} not found. Please verify the ticker/CUSIP is correct.`
        : `Error looking up security: ${error.message || error.response?.data?.error || 'Unknown error'}`;
      throw new Error(errorMsg);
    }

    if (!securityId) {
      throw new Error('Security ID is null');
    }

    // Look up portfolio only if not using allocations
    let portfolioId: number | null = null;
    if (!useAllocations) {
      if (!formData.portfolio || !formData.portfolio.trim()) {
        throw new Error('Portfolio is required');
      }
      const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
      if (!portfolio || !portfolio.id) {
        throw new Error(`Portfolio "${formData.portfolio}" not found`);
      }
      portfolioId = portfolio.id;
    }

    const shares = parseFloat(formData.shares);
    if (!shares || shares <= 0 || isNaN(shares)) {
      throw new Error('Invalid number of shares');
    }

    // Calculate amount based on shares and price
    const price = formData.price ? parseFloat(formData.price) : (marketData?.lastPrice || 0);
    const amount = shares * price;

    // Look up portfolio manager ID
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager && formData.portfolioManager.trim()) {
      const pm = dimensionData.portfolioManagers.find((pm: any) => pm.portfolioManagerName === formData.portfolioManager.trim());
      if (pm && pm.id) {
        portfolioManagerId = pm.id;
      }
    }

    return {
      transactionType: formData.transactionType,
      securityId: securityId,
      portfolioId: portfolioId,
      portfolioManagerId: portfolioManagerId,
      amount: amount,
      quantity: shares,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      price: price || null,
      orderType: formData.orderType || 'MARKET',
      limitPrice: formData.orderType === 'LIMIT' ? parseFloat(formData.limitPrice) : null,
      stopPrice: formData.orderType === 'STOP' ? parseFloat(formData.stopPrice) : null,
      notes: formData.notes || '',
      rationale: formData.rationale || '',
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT'
    };
  };

  const loadTransactionData = (transaction: any) => {
    const tradeDate = transaction.tradeDate || getTodayDate(calendarsFor('PUBLIC_FI', transaction.currency, null, 'EQUITY'));
    // A stored settle date is the server's and is kept; only a missing one is counted here.
    autoSettleRef.current = transaction.settleDate
      ? null
      : provisionalSettleDate(equitySettlementQuery(tradeDate, transaction.currency));
    return {
      transactionType: transaction.transactionType,
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: transaction.assetClassName || transaction.assetClass || 'Equity',
      assetSubClass: transaction.assetSubClassName || transaction.assetSubClass || 'Common Stock',
      security: transaction.securityName || transaction.security || '',
      ticker: transaction.ticker || '',
      cusip: transaction.cusip || '',
      price: transaction.price ? transaction.price.toString() : '',
      limitPrice: transaction.limitPrice ? transaction.limitPrice.toString() : '',
      stopPrice: transaction.stopPrice ? transaction.stopPrice.toString() : '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      shares: transaction.quantity ? transaction.quantity.toString() : '',
      tradeDate,
      settleDate: transaction.settleDate || autoSettleRef.current,
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      currency: transaction.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      rationale: transaction.rationale || '',
      orderType: transaction.orderType || 'MARKET',
      executionBroker: transaction.executionBroker || '',
      ecnPlatform: transaction.ecnPlatform || '',
      executionVenue: transaction.executionVenue || '',
      executionId: transaction.executionId || '',
      executionTime: transaction.executionTime || ''
    };
  };

  // Format currency
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format large numbers
  const formatLargeNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return formatCurrency(value);
  };

  return (
    <BaseTicketScreen
      title="Equity Trade Entry"
      assetClass="Equity"
      hidePortfolioField={true}
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, setErrors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, useAllocations, households, selectedHousehold, setSelectedHousehold, filteredPortfolios }) => {
        const shares = parseFloat(formData.shares) || 0;
        const previewPrice = formData.orderType === 'LIMIT'
          ? parseFloat(formData.limitPrice) || 0
          : formData.orderType === 'STOP'
            ? parseFloat(formData.stopPrice) || 0
            : parseFloat(formData.price) || marketData?.lastPrice || 0;
        const estimatedValue = shares * previewPrice;
        const ticketSide = formData.transactionType || 'BUY';
        const identifier = formData.ticker || formData.cusip || '-';

        return (
        <StructuredWorkspace>
          <EquitySettleDateEffect formData={formData} setFormData={setFormData} autoSettleRef={autoSettleRef} />
          <StructuredHeroPanel
            toneName="equity"
            icon={LineChart}
            eyebrow="Listed Equity Order"
            title={formData.security || 'Equity Ticket'}
            description="Create listed equity orders with ticker validation, OMS ownership, order instructions, and execution-ready pricing before lifecycle approval and posting."
            badges={['Public equity', 'Ticker lookup', 'T+1 settlement', 'IBOR trade lifecycle']}
            signals={[
              { label: 'Side', value: ticketSide, accent: true },
              { label: 'Ticker / CUSIP', value: identifier, accent: Boolean(identifier && identifier !== '-') },
              { label: 'Order Type', value: formData.orderType || 'MARKET' },
              { label: 'Est. Value', value: formatCurrencyValue(estimatedValue, formData.currency || 'USD'), accent: estimatedValue > 0 },
            ]}
          />

          <StructuredBand>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow $tone="equity">Trade Setup</BandEyebrow>
                <BandTitle>Order Dates, Ownership, and Execution Desk</BandTitle>
                <BandSubline>Capture side, trade and settlement timing, manager ownership, and trader assignment before instrument selection.</BandSubline>
              </BandTitleGroup>
            </BandHeader>

          {/* Row 1: Transaction Type, Trade Date, Settle Date */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="transactionType">Transaction Type*</Label>
              <Select
                id="transactionType"
                value={formData.transactionType}
                onChange={(e) => handleInputChange('transactionType', e.target.value)}
                disabled={isFieldDisabled('transactionType')}
              >
                <option value="BUY">Buy</option>
                <option value="SELL">Sell</option>
                <option value="SHORT">Short Sell</option>
                <option value="COVER">Cover Short</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="tradeDate">Trade Date*</Label>
              <DateField
                id="tradeDate"
                value={formData.tradeDate ?? ''}
                onProblem={(problem, fix) => setTradeDateProblem(problem ? { problem, fix } : null)}
                className={errors.tradeDate ? 'error' : ''}
                disabled={isFieldDisabled('tradeDate')}
                calendars={calendarsFor('PUBLIC_FI', formData.currency, null, 'EQUITY')}
                onChange={iso => {
                handleInputChange('tradeDate', iso);
                // Settle date T+1 on the NYSE calendar; the server's answer replaces it (EquitySettleDateEffect)
                if (iso) {
                const settleDate = provisionalSettleDate(equitySettlementQuery(iso, formData.currency));
                autoSettleRef.current = settleDate;
                setFormData((prev: any) => ({ ...prev, settleDate }));
                }
                handleInputBlur('tradeDate', iso);
                }}
              />
              {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="settleDate">Settle Date* (T+1)</Label>
              <DateField
                id="settleDate"
                value={formData.settleDate ?? ''}
                className={errors.settleDate ? 'error' : ''}
                disabled={isFieldDisabled('settleDate')}
                calendars={settlementCalendars({ ticketType: 'PUBLIC_FI', currency: formData.currency, securityType: 'EQUITY' })}
                onChange={iso => handleInputChange('settleDate', iso)}
              />
              {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
            </FormGroup>
          </FormRow>

          {/* Row 2: Asset Manager (institutional only), Portfolio Manager/Advisor, Executed By */}
          <FormRow>
            {/* Asset Manager - Hide for retail clients */}
            {!isRetail && (
              <FormGroup>
                <Label htmlFor="assetManager">Asset Manager*</Label>
                {isFieldDisabled('assetManager') && formData.assetManager ? (
                  <Input type="text" value={formData.assetManager} disabled />
                ) : (
                  <Select
                    id="assetManager"
                    value={formData.assetManager}
                    onChange={(e) => handleInputChange('assetManager', e.target.value)}
                    className={errors.assetManager ? 'error' : ''}
                    disabled={isFieldDisabled('assetManager')}
                  >
                    <option value="">Select Asset Manager</option>
                    {dimensionData.assetManagers.map((am: any) => (
                      <option key={am.id} value={am.assetManagerName}>
                        {am.assetManagerName}
                      </option>
                    ))}
                  </Select>
                )}
                {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="portfolioManager">{isRetail ? 'Advisor*' : 'Portfolio Manager*'}</Label>
              <Select
                id="portfolioManager"
                value={formData.portfolioManager}
                onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
                onBlur={(e) => handleInputBlur('portfolioManager', e.target.value)}
                className={errors.portfolioManager ? 'error' : ''}
                disabled={isFieldDisabled('portfolioManager')}
              >
                <option value="">{isRetail ? 'Select Advisor' : 'Select Portfolio Manager'}</option>
                {dimensionData.portfolioManagers.map((pm: any) => (
                  <option key={pm.id} value={pm.portfolioManagerName}>
                    {pm.portfolioManagerName}
                  </option>
                ))}
              </Select>
              {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
              {(transactionStatus === 'EXECUTED' || transactionStatus === 'CANCELLED') && formData.executedBy ? (
                <Input type="text" value={formData.executedBy} disabled />
              ) : (
                <Select
                  id="executedBy"
                  value={formData.executedBy}
                  onChange={(e) => handleInputChange('executedBy', e.target.value)}
                  onBlur={(e) => handleInputBlur('executedBy', e.target.value)}
                  disabled={transactionStatus === 'EXECUTED' || transactionStatus === 'CANCELLED'}
                  className={errors.executedBy ? 'error' : ''}
                >
                  <option value="">Select</option>
                  {dimensionData.users && dimensionData.users.length > 0 ? (
                    dimensionData.users.map((user: any) => (
                      <option key={user.id} value={user.fullName}>
                        {user.fullName} {user.role ? `(${user.role})` : ''}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Loading users...</option>
                  )}
                </Select>
              )}
              {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
            </FormGroup>
          </FormRow>

          {/* Execution Venue Details - Only show when executed via FIX */}
          {(formData.ecnPlatform || formData.executionId || formData.executionTime) && (
            <ExecutionNotice>
            <FormRow>
              <FormGroup>
                <Label style={{ color: '#22c55e' }}>ECN Platform</Label>
                <Input
                  type="text"
                  value={formData.ecnPlatform || '-'}
                  disabled
                  style={{
                    background: 'rgba(30, 41, 59, 0.6)',
                    fontWeight: 600,
                    color: '#22c55e'
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label style={{ color: '#22c55e' }}>Execution Broker</Label>
                <Input
                  type="text"
                  value={formData.executionBroker || '-'}
                  disabled
                  style={{
                    background: 'rgba(30, 41, 59, 0.6)',
                    fontWeight: 600,
                    color: '#22c55e'
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label style={{ color: '#22c55e' }}>Execution ID</Label>
                <Input
                  type="text"
                  value={formData.executionId || '-'}
                  disabled
                  style={{
                    background: 'rgba(30, 41, 59, 0.6)',
                    fontWeight: 500,
                    color: '#94a3b8',
                    fontSize: '0.85rem'
                  }}
                />
              </FormGroup>
            </FormRow>
            </ExecutionNotice>
          )}

          </StructuredBand>

          <StructuredBand>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow $tone="equity">Instrument</BandEyebrow>
                <BandTitle>Ticker, Security Master, Portfolio, and Quantity</BandTitle>
                <BandSubline>Search the security master by ticker or CUSIP, then define the target portfolio and share quantity.</BandSubline>
              </BandTitleGroup>
            </BandHeader>

          {/* Row 3: Ticker, Security Name, Asset Class */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="ticker">Ticker / CUSIP*</Label>
              <AutocompleteWrapper>
                <Input
                  id="ticker"
                  type="text"
                  value={formData.ticker || formData.cusip}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    handleInputChange('ticker', value);
                    if (value.trim().length >= 1) {
                      fetchTickerSuggestions(value.trim());
                    } else {
                      setTickerSuggestions([]);
                      setShowTickerSuggestions(false);
                    }
                  }}
                  onBlur={(e) => {
                    handleInputBlur('ticker', e.target.value);
                    setTimeout(() => setShowTickerSuggestions(false), 200);
                  }}
                  onFocus={() => {
                    const value = formData.ticker || formData.cusip;
                    if (value && value.trim().length >= 1) {
                      fetchTickerSuggestions(value.trim());
                      setShowTickerSuggestions(true);
                    }
                  }}
                  placeholder="Enter ticker (e.g., AAPL, MSFT)"
                  className={errors.ticker ? 'error' : ''}
                  disabled={isFieldDisabled('ticker')}
                  autoComplete="off"
                />
                {showTickerSuggestions && tickerSuggestions.length > 0 && (
                  <AutocompleteDropdown>
                    {tickerSuggestions.map((security) => (
                      <SuggestionItem
                        key={security.id}
                        onClick={() => handleTickerSelect(security, setFormData, setErrors, errors, dimensionData)}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <SuggestionCusip>{security.ticker || security.cusip}</SuggestionCusip>
                        <SuggestionSecurity>{security.securityName || 'No name'}</SuggestionSecurity>
                      </SuggestionItem>
                    ))}
                  </AutocompleteDropdown>
                )}
              </AutocompleteWrapper>
              {errors.ticker && <FieldError>{errors.ticker}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="security">Security*</Label>
              <Input
                id="security"
                type="text"
                value={formData.security}
                onChange={(e) => handleInputChange('security', e.target.value)}
                onBlur={(e) => handleInputBlur('security', e.target.value)}
                placeholder="Security name"
                className={errors.security ? 'error' : ''}
                disabled={isFieldDisabled('security')}
              />
              {errors.security && <FieldError>{errors.security}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="assetSubClass">Asset Sub Class</Label>
              <Select
                id="assetSubClass"
                value={formData.assetSubClass}
                onChange={(e) => handleInputChange('assetSubClass', e.target.value)}
                disabled={isFieldDisabled('assetSubClass')}
              >
                <option value="Common Stock">Common Stock</option>
                <option value="Preferred Stock">Preferred Stock</option>
                <option value="ADR">ADR</option>
                <option value="ETF">ETF</option>
                <option value="REIT">REIT</option>
              </Select>
            </FormGroup>
          </FormRow>

          {/* Row 4: Portfolio/Account (if not using allocations), Shares, Currency */}
          <FormRow>
            {!useAllocations && (
              isRetail ? (
                /* Retail: Household + Client Account */
                <>
                  <FormGroup>
                    <Label htmlFor="household">Household</Label>
                    <Select
                      id="household"
                      value={selectedHousehold}
                      onChange={(e) => setSelectedHousehold(e.target.value)}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">All Households</option>
                      {households.map((household: any) => (
                        <option key={household.id} value={household.id.toString()}>
                          {household.householdName || household.name}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="portfolio">Client Account*</Label>
                    <Select
                      id="portfolio"
                      value={formData.portfolio}
                      onChange={(e) => handleInputChange('portfolio', e.target.value)}
                      className={errors.portfolio ? 'error' : ''}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">Select Account</option>
                      {(selectedHousehold ? filteredPortfolios : dimensionData.portfolios).map((p: any) => (
                        <option key={p.id} value={p.portfolioName}>
                          {p.portfolioName}
                        </option>
                      ))}
                    </Select>
                    {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                  </FormGroup>
                </>
              ) : (
                /* Institutional: Portfolio */
                <FormGroup>
                  <Label htmlFor="portfolio">Portfolio*</Label>
                  <Select
                    id="portfolio"
                    value={formData.portfolio}
                    onChange={(e) => handleInputChange('portfolio', e.target.value)}
                    className={errors.portfolio ? 'error' : ''}
                    disabled={isFieldDisabled('portfolio')}
                  >
                    <option value="">Select Portfolio</option>
                    {dimensionData.portfolios.map((p: any) => (
                      <option key={p.id} value={p.portfolioName}>
                        {p.portfolioName}
                      </option>
                    ))}
                  </Select>
                  {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                </FormGroup>
              )
            )}

            <FormGroup>
              <Label htmlFor="shares">Number of Shares*</Label>
              <Input
                id="shares"
                type="text"
                value={formData.shares}
                onChange={(e) => handleInputChange('shares', e.target.value)}
                onBlur={(e) => {
                  const parsed = parseAmountShorthand(e.target.value);
                  if (parsed > 0) {
                    setFormData((prev: any) => ({ ...prev, shares: parsed.toString() }));
                  }
                  handleInputBlur('shares', parsed.toString());
                }}
                placeholder="Enter shares (e.g., 100, 1K)"
                className={errors.shares ? 'error' : ''}
                disabled={isFieldDisabled('shares')}
              />
              {errors.shares && <FieldError>{errors.shares}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="currency">Currency</Label>
              <Select
                id="currency"
                value={formData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                disabled={isFieldDisabled('currency') || !!formData.ticker}
              >
                <option value="">Select Currency</option>
                {dimensionData.currencies.map((currency: any) => (
                  <option key={currency.id} value={currency.currencyCode}>
                    {currency.currencyCode} - {currency.currencyName}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>

          </StructuredBand>

          <StructuredBand>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow $tone="equity">Order Instructions</BandEyebrow>
                <BandTitle>Order Type, Price Controls, and Rationale</BandTitle>
                <BandSubline>Set market, limit, or stop instructions and review estimated consideration before submitting the ticket.</BandSubline>
              </BandTitleGroup>
            </BandHeader>

          <TradeEconomicsGrid>
            <div>

          {/* Row 5: Order Type and Price */}
          <FormRow>
            <FormGroup style={{ gridColumn: 'span 1' }}>
              <Label>Order Type</Label>
              <OrderTypeSelector>
                {(['MARKET', 'LIMIT', 'STOP'] as const).map((type) => (
                  <OrderTypeButton
                    key={type}
                    type="button"
                    $active={formData.orderType === type}
                    onClick={() => {
                      setFormData((prev: any) => ({ ...prev, orderType: type }));
                    }}
                    disabled={isFieldDisabled('orderType')}
                  >
                    {type}
                  </OrderTypeButton>
                ))}
              </OrderTypeSelector>
            </FormGroup>

            {formData.orderType === 'MARKET' && (
              <FormGroup>
                <Label htmlFor="price">Price{transactionStatus === 'SENT' ? '*' : ''}</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder={marketData?.lastPrice ? `Market: ${formatCurrency(marketData.lastPrice)}` : 'Market price'}
                  className={errors.price ? 'error' : ''}
                  disabled={isFieldDisabled('price')}
                  step="0.01"
                />
                {errors.price && <FieldError>{errors.price}</FieldError>}
              </FormGroup>
            )}

            {formData.orderType === 'LIMIT' && (
              <FormGroup>
                <Label htmlFor="limitPrice">Limit Price*</Label>
                <Input
                  id="limitPrice"
                  type="number"
                  value={formData.limitPrice}
                  onChange={(e) => handleInputChange('limitPrice', e.target.value)}
                  placeholder="Enter limit price"
                  className={errors.limitPrice ? 'error' : ''}
                  disabled={isFieldDisabled('limitPrice')}
                  step="0.01"
                />
                {errors.limitPrice && <FieldError>{errors.limitPrice}</FieldError>}
              </FormGroup>
            )}

            {formData.orderType === 'STOP' && (
              <FormGroup>
                <Label htmlFor="stopPrice">Stop Price*</Label>
                <Input
                  id="stopPrice"
                  type="number"
                  value={formData.stopPrice}
                  onChange={(e) => handleInputChange('stopPrice', e.target.value)}
                  placeholder="Enter stop price"
                  className={errors.stopPrice ? 'error' : ''}
                  disabled={isFieldDisabled('stopPrice')}
                  step="0.01"
                />
                {errors.stopPrice && <FieldError>{errors.stopPrice}</FieldError>}
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="submittedBy">Submitted By</Label>
              <Input
                id="submittedBy"
                type="text"
                value={formData.submittedBy}
                onChange={(e) => handleInputChange('submittedBy', e.target.value)}
                disabled={isFieldDisabled('submittedBy')}
                placeholder="Enter submitted by"
              />
            </FormGroup>
          </FormRow>

          {/* Market Data Panel - Show when ticker is selected */}
          {(formData.ticker || formData.cusip) && (marketData || marketDataLoading) && (
            <MarketDataPanel>
              <MarketDataTitle><BarChart3 size={15} /> Market Data</MarketDataTitle>
              {marketDataLoading ? (
                <LoadingMessage>Loading market data...</LoadingMessage>
              ) : marketData ? (
                <MarketDataGrid>
                  <MarketDataCard>
                    <MarketDataLabel>Last Price</MarketDataLabel>
                    <MarketDataValue>{formatCurrency(marketData.lastPrice)}</MarketDataValue>
                    <MarketDataChange
                      positive={marketData.change > 0}
                      negative={marketData.change < 0}
                    >
                      {marketData.change > 0 ? '+' : ''}{formatCurrency(marketData.change)} ({marketData.changePercent?.toFixed(2)}%)
                    </MarketDataChange>
                  </MarketDataCard>
                  <MarketDataCard>
                    <MarketDataLabel>Bid / Ask</MarketDataLabel>
                    <MarketDataValue>
                      {formatCurrency(marketData.bid)} / {formatCurrency(marketData.ask)}
                    </MarketDataValue>
                  </MarketDataCard>
                  <MarketDataCard>
                    <MarketDataLabel>Volume</MarketDataLabel>
                    <MarketDataValue>{marketData.volume?.toLocaleString() || '-'}</MarketDataValue>
                    <MarketDataChange>Avg: {marketData.avgVolume?.toLocaleString() || '-'}</MarketDataChange>
                  </MarketDataCard>
                  <MarketDataCard>
                    <MarketDataLabel>52 Week Range</MarketDataLabel>
                    <MarketDataValue>
                      {formatCurrency(marketData.low52Week)} - {formatCurrency(marketData.high52Week)}
                    </MarketDataValue>
                  </MarketDataCard>
                </MarketDataGrid>
              ) : null}
            </MarketDataPanel>
          )}

          {/* Stock Metrics Panel - Show when stock metrics are available */}
          {stockMetrics && (
            <StockMetricsPanel>
              <StockMetricsTitle><Briefcase size={15} /> Stock Metrics</StockMetricsTitle>
              <StockMetricsGrid>
                <MetricCard>
                  <MetricLabel>Market Cap</MetricLabel>
                  <MetricValue>{formatLargeNumber(stockMetrics.marketCap)}</MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>P/E Ratio</MetricLabel>
                  <MetricValue>{stockMetrics.peRatio?.toFixed(2) || '-'}</MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Dividend Yield</MetricLabel>
                  <MetricValue>{stockMetrics.dividendYield ? `${stockMetrics.dividendYield.toFixed(2)}%` : '-'}</MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Beta</MetricLabel>
                  <MetricValue>{stockMetrics.beta?.toFixed(2) || '-'}</MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>EPS</MetricLabel>
                  <MetricValue>{formatCurrency(stockMetrics.eps)}</MetricValue>
                </MetricCard>
              </StockMetricsGrid>
            </StockMetricsPanel>
          )}

          {/* Notes Row */}
          <FormRow>
            <FormGroup style={{ gridColumn: 'span 2' }}>
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                type="text"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Add notes..."
                disabled={isFieldDisabled('notes')}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="rationale">Trade Rationale</Label>
              <Input
                id="rationale"
                type="text"
                value={formData.rationale}
                onChange={(e) => handleInputChange('rationale', e.target.value)}
                placeholder="Rationale..."
                disabled={isFieldDisabled('rationale')}
              />
            </FormGroup>
          </FormRow>
            </div>

            <EconomicsPreview
              toneName="equity"
              title="Order Economics"
              notionalLabel="Shares"
              notionalValue={shares}
              price={previewPrice}
              priceLabel="Price / Share"
              priceValue={previewPrice > 0 ? formatCurrency(previewPrice) : '-'}
              consideration={estimatedValue}
              currency={formData.currency || 'USD'}
              extraMetrics={[
                { label: 'Side', value: ticketSide, accent: true },
                { label: 'Order Type', value: formData.orderType || 'MARKET' },
                { label: 'Identifier', value: identifier },
                { label: 'Settlement', value: formData.settleDate || '-' },
                { label: 'Quantity', value: formatNumberValue(shares) },
              ]}
            />
          </TradeEconomicsGrid>
          </StructuredBand>
        </StructuredWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default EquityTicketScreen;
